import { DriveType } from "./mapAssetRow";

/** How full a vehicle is, and how it is powered, shown beside its name on the map. */
export interface AssetLabelBadge {
  driveType: DriveType;
  /** The short word for the drive type, when the reader asked for it. */
  powertrainLabel?: string;
  /** Battery for an electric vehicle, tank otherwise. Left out when the vehicle reports neither. */
  energyPercent?: number;
  isCharging: boolean;
}

/**
 * Size of the badge icons and the gaps around them.
 *
 * The layout reserves exactly the box that gets drawn, so the code that measures a name box and
 * the code that draws it have to work from the same numbers. They live here, in neither of them,
 * so that neither has to reach into the other.
 */
export const BADGE_ICON_PX = 14;
export const BADGE_GAP_PX = 3;
/** Padding either side of the drive-type word, matching what the chip is drawn with. */
export const BADGE_TEXT_PADDING_PX = 3;

/**
 * The short word for how a vehicle is powered.
 *
 * The words the fleet already uses: a battery vehicle is a BEV, a combustion one an ICE, and a
 * fuel cell goes by its fuel.
 */
export const POWERTRAIN_LABELS: Record<DriveType, string> = {
  electric: "BEV",
  combustion: "ICE",
  hydrogen: "H2",
};

/**
 * The percentage as it is written on a label, or an empty string when there is no level to show.
 *
 * Shared for the same reason as the sizes: the box is measured from this text and then drawn with
 * it, and a difference between the two shows up as a clipped name.
 */
export function badgePercentText(badge: AssetLabelBadge | undefined): string {
  return badge?.energyPercent === undefined ? "" : `${Math.round(badge.energyPercent)}%`;
}

/**
 * How many things sit in a name box, counting the name itself.
 *
 * A gap goes between each neighbouring pair, so the count is what turns the gap size into a
 * width. The drive-type icon and its percentage are two items, not one.
 */
export function badgeItemCount(badge: AssetLabelBadge | undefined): number {
  if (!badge) {
    return 1;
  }
  return (
    1 +
    (badge.powertrainLabel ? 1 : 0) +
    (badge.isCharging ? 1 : 0) +
    (badge.energyPercent === undefined ? 0 : 2)
  );
}
